import { useCallback, useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import {
  api,
  getActiveWorkspaceId,
  setActiveWorkspaceId,
  type AccountsResponse,
  type BillingState,
  type DispatchRecord,
  type Notification,
  type PlatformId,
  type PostDraft,
  type ScheduledPost,
  type TikTokOptions,
  type Workspace,
} from "./api";
import { useDispatchTracking } from "./lib/useDispatchTracking";
import { browserTimezone, isValidTimezone } from "./lib/tz";
import { Login } from "./components/Login";
import { ResetPassword } from "./components/ResetPassword";
import { Onboarding } from "./components/Onboarding";
import { Composer } from "./components/Composer";
import { ControlCenter } from "./components/ControlCenter";
import { Accounts } from "./components/Accounts";
import { StatusBanner } from "./components/StatusBanner";
import { AnalyticsBubble } from "./components/AnalyticsBubble";
import { ConnectionBar } from "./components/ConnectionBar";
import { Guide } from "./components/Guide";
import { HelpPanel } from "./components/HelpPanel";
import { WorkspaceSwitcher } from "./components/WorkspaceSwitcher";
import { Calendar } from "./components/Calendar";
import { BulkPlatformBar } from "./components/BulkPlatformBar";
import { SlideOver } from "./components/SlideOver";
import { Notifications } from "./components/Notifications";
import { Toasts, type Toast } from "./components/Toasts";
import { OpsDashboard } from "./components/OpsDashboard";
import { WelcomeTour } from "./components/WelcomeTour";
import { Activity } from "./components/Activity";
import { Sidebar, type NavKey } from "./components/Sidebar";
import { TopBar } from "./components/TopBar";
import { DashboardHeader } from "./components/DashboardHeader";
import { TodayRail } from "./components/TodayRail";
import { RecentPerformance } from "./components/RecentPerformance";
import { CommandPalette } from "./components/CommandPalette";
import { Analytics } from "./components/Analytics";
import {
  computeStats,
  clearPost,
  dismissFailure,
  firstNameFromEmail,
  greeting,
  reconnectAccounts,
  recentFailures,
  readClearedPosts,
  readDismissedFailures,
  restoreAllClearedPosts,
  todaysScheduled,
  unclearPost,
} from "./lib/commandCenter";

/** Proper display names for the OAuth banner, which only receives the raw
 * platform id from the callback (?detail=tiktok). Falls back to the id. */
const PLATFORM_LABEL: Record<string, string> = {
  youtube: "YouTube",
  tiktok: "TikTok",
  instagram: "Instagram",
  x: "X",
  facebook: "Facebook",
};

const SIDEBAR_COLLAPSE_KEY = "broadside.sidebar.collapsed";

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  // Password-recovery flow: when the user arrives from an emailed reset link,
  // Supabase mints a recovery session and fires a PASSWORD_RECOVERY event. We
  // surface a dedicated "set a new password" screen instead of the dashboard.
  // Seeded from the URL so landing straight on the reset link never flashes the
  // dashboard before the event arrives.
  const [recovery, setRecovery] = useState<boolean>(() => {
    try {
      return (window.location.hash || "").includes("type=recovery");
    } catch {
      return false;
    }
  });
  // Whether the current session still owes a second factor (signed in at aal1
  // while a verified TOTP factor exists). Derived LOCALLY from the session — for
  // anyone with no factor it is always false, so their login flow is unchanged.
  const [mfaPending, setMfaPending] = useState(false);
  const [data, setData] = useState<AccountsResponse | null>(null);
  // Billing state is loaded here (not only inside the Billing panel) so the
  // composer can gate on the live credit balance — a post spends a
  // platform-weighted amount of credits (see lib/credits). Null until first
  // load, or when billing is unreachable.
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  // The idempotency key of an immediate publish still landing in the background.
  // App owns the durable poll/refresh (see useDispatchTracking) so it survives
  // the compose panel being closed mid-flight.
  const [inFlightKey, setInFlightKey] = useState<string | null>(null);
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState<string | null>(
    getActiveWorkspaceId(),
  );
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  // Push a top-of-screen toast. "Smart": an informational toast (no action)
  // auto-dismisses after a few seconds; an actionable one (with an `action`) is
  // sticky and stays until the user acts on it or closes it.
  const pushToast = useCallback((t: Omit<Toast, "id">): string => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { ...t, id }]);
    // Default: actionable toasts stick, informational ones fade — but an explicit
    // `sticky` wins (e.g. an Undo toast has an action yet should still fade).
    const sticky = t.sticky ?? Boolean(t.action);
    if (!sticky) {
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== id));
      }, 6000);
    }
    return id;
  }, []);
  // Back-compat shim so existing "banner" call sites keep working as toasts.
  const setBanner = useCallback(
    (b: { kind: "ok" | "err"; text: string } | null) => {
      if (b) pushToast({ kind: b.kind, text: b.text });
    },
    [pushToast],
  );

  // The effective IANA timezone for interpreting/displaying scheduled times:
  // the user's saved choice, or their browser's zone until they pick one. Every
  // time conversion in the app routes through this (see lib/tz), so a user who
  // never opens the picker sees byte-identical browser-local behavior.
  const [timezone, setTimezoneState] = useState<string>(() => browserTimezone());

  // Which primary section is showing in the workspace area.
  const [nav, setNav] = useState<NavKey>("calendar");
  // Sidebar collapse (persisted) + command palette visibility.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Slide-over visibility state. (Settings and Create post are full pages in the
  // workspace area now, reached via `nav === "settings"` / `nav === "compose"`.)
  const [guideOpen, setGuideOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  // First-run welcome tour: dismissed this session, or previously (localStorage,
  // checked per-account at render). Set true when the user closes/completes it.
  const [tourDismissed, setTourDismissed] = useState(false);

  // Notifications inbox + unread badge. Loaded alongside the dashboard refresh
  // (no separate poller) and when the panel opens.
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  // The scheduled post currently being edited, if any. Non-null puts the
  // composer in edit mode (hydrated from the post's draft); saving reschedules.
  const [editingPost, setEditingPost] = useState<ScheduledPost | null>(null);
  const [repostFrom, setRepostFrom] = useState<{
    draft: PostDraft;
    platforms: PlatformId[];
    nonce: number;
  } | null>(null);

  // Needs-attention failures the user has dismissed (persisted in localStorage),
  // excluded from the rail so a resolved failure stops nagging across refreshes.
  const [dismissedFailures, setDismissedFailures] = useState<Set<string>>(() =>
    readDismissedFailures(),
  );
  // Published posts the user has hidden from the calendar (kept in Analytics).
  const [clearedPosts, setClearedPosts] = useState<Set<string>>(() => readClearedPosts());
  const handleClearPost = useCallback(
    (id: string) => {
      setClearedPosts(clearPost(id));
      pushToast({
        kind: "info",
        text: "Post hidden from the calendar.",
        sticky: false,
        action: { label: "Undo", onClick: () => setClearedPosts(unclearPost(id)) },
      });
    },
    [pushToast],
  );
  const handleRestoreCleared = useCallback(() => {
    setClearedPosts(restoreAllClearedPosts());
  }, []);
  // A "View details" request from the attention rail: which failed dispatch to
  // reveal in the calendar. A fresh object per click re-triggers the calendar's
  // focus effect, so the same failure can be re-opened after closing.
  const [calendarFocus, setCalendarFocus] = useState<{ at: number; id: string } | null>(null);

  // Apply a session AND its MFA assurance together, in one async tick, so the
  // dashboard never flashes before a second factor is satisfied. The assurance
  // check is local (it reads the session's own factors — no network), so a user
  // with no enrolled factor resolves to `nextLevel === currentLevel` and is
  // never gated: their flow is byte-for-byte unchanged.
  // Tracks whether a session was already present, so we can tell a real sign-in
  // (absent → present) apart from a token refresh (present → present). Only the
  // former resets the view; a background refresh must never yank the user's page.
  const wasSignedInRef = useRef(false);
  const applySession = useCallback(async (s: Session | null) => {
    if (!s) {
      wasSignedInRef.current = false;
      setMfaPending(false);
      setSession(null);
      return;
    }
    let pending = false;
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      pending = Boolean(data) && data!.nextLevel === "aal2" && data!.nextLevel !== data!.currentLevel;
    } catch {
      // If the check itself fails, don't lock the user out — proceed unguarded.
      pending = false;
    }
    setMfaPending(pending);
    setSession(s);
    // Track that a session is now present. The Dashboard reset lives in the auth
    // listener (on the SIGNED_IN event only), NOT here: resetting on every
    // absent→present transition also fired for the INITIAL_SESSION restored at
    // page load, and if that async restore resolved after the user had already
    // clicked a nav item it clobbered them back to the Dashboard (a real race).
    wasSignedInRef.current = true;
  }, []);

  // Track the Supabase auth session.
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) =>
      applySession(data.session).finally(() => setReady(true)),
    );
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // A reset-link arrival fires PASSWORD_RECOVERY with a fresh recovery
      // session — route to the set-new-password screen rather than the app.
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
      // Land on the Dashboard on a real sign-in ONLY. `SIGNED_IN` fires for an
      // actual login (Supabase uses the separate `INITIAL_SESSION` event for the
      // page-load restore, and `TOKEN_REFRESHED` for refreshes), and the
      // wasSignedInRef guard makes sure it's a logged-out→logged-in transition —
      // so this never clobbers navigation on load or on a background refresh.
      if (event === "SIGNED_IN" && !wasSignedInRef.current) setNav("calendar");
      void applySession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, [applySession]);

  // Load the saved timezone once the user is signed in — a one-time read (not on
  // the frequent refresh cadence). Falls back to the browser zone the state was
  // seeded with. Best-effort: a failure just keeps the browser default.
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    void api
      .preferences()
      .then((p) => {
        if (!cancelled && p.timezone && isValidTimezone(p.timezone)) setTimezoneState(p.timezone);
      })
      .catch(() => {
        /* keep the browser-default zone */
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Persist a timezone choice: update the UI immediately (optimistic), then save.
  // A failed save reverts so the picker never lies about what's stored.
  const handleSetTimezone = useCallback(
    async (tz: string) => {
      const prev = timezone;
      setTimezoneState(tz);
      try {
        await api.setTimezone(tz);
      } catch {
        setTimezoneState(prev);
        pushToast({ kind: "err", text: "Couldn't save your timezone — try again." });
      }
    },
    [timezone, pushToast],
  );

  // Best-effort: fetch the inbox + unread count. Failures are swallowed — the
  // badge simply stays at its last value rather than breaking the dashboard.
  const loadNotifications = useCallback(async () => {
    try {
      const res = await api.notifications();
      setNotifications(res.notifications);
      setUnread(res.unread);
    } catch {
      /* leave the badge as-is */
    }
  }, []);

  const refresh = useCallback(async () => {
    // Before any session exists there's nothing to load and no error to show —
    // the auth listener calls refresh() again once signed in.
    let sess: Awaited<ReturnType<typeof supabase.auth.getSession>>["data"];
    try {
      ({ data: sess } = await supabase.auth.getSession());
    } catch {
      // getSession can reject on a network hiccup or corrupted local storage.
      // refresh() is called fire-and-forget (`void refresh()`, and from the
      // dispatch-tracking poll), so an escaping rejection would surface as an
      // unhandled promise rejection. Treat it as "not ready yet" — the auth
      // listener re-runs refresh() once a session is available.
      return;
    }
    if (!sess.session) return;
    // Refresh the unread count on the existing dashboard cadence (piggyback, not
    // a heavy independent poller).
    void loadNotifications();
    try {
      const accounts = await api.accounts();
      setData(accounts);
    } catch (err) {
      // A real failure once signed in (network down, server 500) must NOT be
      // swallowed as "not signed in" — surface it so the dashboard doesn't sit
      // blank and silent. The banner is dismissible.
      setBanner({
        kind: "err",
        text: err instanceof Error ? err.message : "Couldn't load your dashboard.",
      });
    }
    // Billing is best-effort: its own panel reports load failures, and the
    // composer simply skips credit gating when it's unavailable.
    try {
      setBilling(await api.billing());
    } catch {
      setBilling(null);
    }
  }, [loadNotifications]);

  // Durable tracking of an in-flight immediate publish — outlives the compose
  // page so the calendar/History still refresh if the user navigates away
  // mid-flight (#1). When the user is NOT on the compose page — they left, or
  // hid the composer's own result overlay — a backgrounded publish would finish
  // invisibly, so announce it here with a toast that jumps to the Activity log.
  // On the compose page the composer's overlay already shows the outcome, so we
  // stay silent to avoid double-signalling.
  const handleDispatchDone = useCallback(
    (completed: boolean) => {
      setInFlightKey(null);
      if (nav === "compose") return;
      pushToast(
        completed
          ? {
              kind: "ok",
              text: "Your post published.",
              action: { label: "View in Activity", onClick: () => setNav("activity") },
            }
          : {
              kind: "warn",
              text: "Still publishing — it'll appear in your Activity log shortly.",
              action: { label: "Open Activity", onClick: () => setNav("activity") },
            },
      );
    },
    [nav, pushToast],
  );
  useDispatchTracking(inFlightKey, refresh, handleDispatchDone);

  // Open the composer for a brand-new post — clears any lingering edit target AND
  // any re-post draft so "Create post" / "New post" / "Import folder" never
  // inherit a previously-edited (or re-posted) post's mode or content.
  const openCompose = useCallback(() => {
    setEditingPost(null);
    setRepostFrom(null);
    setNav("compose");
  }, []);

  // Open the notifications inbox. Opening it fetches fresh, then marks everything
  // read (the act of opening is the acknowledgement) and clears the badge.
  // Mark everything read + clear the badge — shared by the bell dropdown and the
  // full Notifications page, so viewing either clears the unread count.
  const markNotificationsViewed = useCallback(async () => {
    await loadNotifications();
    setUnread(0);
    setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: Date.now() })));
    try {
      await api.markNotificationRead("all");
    } catch {
      /* the count reconciles on the next refresh */
    }
  }, [loadNotifications]);

  const openNotifications = useCallback(() => {
    setNotifOpen(true);
    void markNotificationsViewed();
  }, [markNotificationsViewed]);

  // Opening the full Notifications page clears the badge too.
  useEffect(() => {
    if (nav === "notifications") void markNotificationsViewed();
  }, [nav, markNotificationsViewed]);

  // Any navigation closes the transient right-hand slide-overs (the notifications
  // peek, the guide) — otherwise clicking a sidebar item while the notifications
  // peek is open lands you on the new page with the peek still stuck open.
  // Opening a peek doesn't change `nav`, so this never fights the opener.
  useEffect(() => {
    setNotifOpen(false);
    setGuideOpen(false);
    setHelpOpen(false);
    // A page switch returns to the top — a deep-scrolled page (e.g. a long
    // calendar) must not hand the next page its leftover scroll position, which
    // made "Open Activity log" from a batch popup land mid-page. The document
    // itself scrolls (no inner overflow container), so reset the window.
    window.scrollTo({ top: 0 });
  }, [nav]);

  // Persist + toggle the sidebar collapse.
  const toggleCollapse = useCallback(() => {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* non-fatal */
      }
      return next;
    });
  }, []);

  // Global shortcuts: ⌘K / Ctrl+K toggles the command palette; N (when not
  // typing and no overlay is open) starts a new post.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      if (!mod && (e.key === "n" || e.key === "N")) {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
        if (paletteOpen || nav === "compose" || guideOpen || notifOpen) return;
        e.preventDefault();
        openCompose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paletteOpen, nav, guideOpen, notifOpen, openCompose]);

  const handleMarkAllRead = useCallback(() => {
    setUnread(0);
    setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: Date.now() })));
    void api.markNotificationRead("all").catch(() => {
      /* reconciles on next refresh */
    });
  }, []);

  const handleDismissNotification = useCallback(
    (id: string) => {
      // Optimistic removal; on failure reload so the list reflects the server.
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      void api.dismissNotification(id).catch(() => loadNotifications());
    },
    [loadNotifications],
  );

  // "View details" on a failed attention item → reveal that post in the
  // calendar, whose day-detail slide-over shows each platform's real error. A
  // fresh focus object per click guarantees the effect re-fires.
  const handleViewFailure = useCallback((rec: DispatchRecord) => {
    setCalendarFocus({ at: rec.at, id: rec.id });
    setNav("calendar");
  }, []);

  // Dismiss a failed attention item: persist the id so it stays gone, then
  // best-effort clear any matching `dispatch_failed` inbox notification so the
  // bell badge stays consistent (reuses the same optimistic path as the inbox).
  const handleDismissFailure = useCallback(
    (rec: DispatchRecord) => {
      setDismissedFailures(dismissFailure(rec.id));
      const match = notifications.find(
        (n) => n.type === "dispatch_failed" && n.ref?.dispatchId === rec.id,
      );
      if (match) handleDismissNotification(match.id);
    },
    [notifications, handleDismissNotification],
  );

  // Deep-link an inbox item to the surface that resolves it. `reconnect_needed`
  // opens Settings (where the account can be reconnected); other types simply
  // close the panel (their detail is in the body, e.g. History below).
  const handleNotificationNavigate = useCallback(
    (n: Notification) => {
      setNotifOpen(false);
      if (n.type === "reconnect_needed") {
        setNav("connections");
      } else if (n.type === "credits_low") {
        setNav("settings");
      } else if (n.type === "dispatch_failed") {
        // Notifications span ALL workspaces (see listNotifications), so a failure
        // can belong to a different brand than the active one. Jump to the post's
        // OWN workspace first — otherwise "View details" lands on the current
        // brand's calendar where the post doesn't exist (the bug this fixes).
        const ws = typeof n.ref?.workspaceId === "string" ? n.ref.workspaceId : null;
        const dispatchId = typeof n.ref?.dispatchId === "string" ? n.ref.dispatchId : null;
        const refAt = typeof n.ref?.at === "number" ? n.ref.at : null;
        if (ws && ws !== activeWorkspaceId) {
          setActiveWorkspaceId(ws);
          setActiveWorkspaceIdState(ws);
          setEditingPost(null);
          void refresh();
        }
        // Focus the post by time + id. ref.at works even before the switched
        // workspace's data has loaded; else fall back to the current history.
        const rec = dispatchId ? data?.history.find((r) => r.id === dispatchId) ?? null : null;
        const focusAt = refAt ?? rec?.at ?? null;
        if (dispatchId && focusAt != null) setCalendarFocus({ at: focusAt, id: dispatchId });
        setNav("calendar");
      }
    },
    [data, activeWorkspaceId, refresh],
  );

  // A notification's brand name, for the inbox chip — only when the user has more
  // than one workspace (otherwise it's noise). Notifications span all workspaces,
  // so this tells the user WHICH one a failure belongs to.
  const resolveWorkspaceName = useCallback(
    (id: string): string | null =>
      workspaces.length > 1 ? workspaces.find((w) => w.id === id)?.name ?? null : null,
    [workspaces],
  );
  // The active brand's name — shown on page heads (Planner/Activity) ONLY when the
  // user has more than one workspace, so single-brand accounts see no noise, and a
  // multi-brand user always knows whose posts they're looking at.
  const activeBrandName =
    activeWorkspaceId != null ? resolveWorkspaceName(activeWorkspaceId) : null;

  // The ONE place a scheduled post is cancelled, so the confirm prompt is
  // guaranteed on every surface (banner + calendar) rather than duplicated.
  const handleCancelScheduled = useCallback(
    async (id: string) => {
      if (!window.confirm("Cancel this scheduled post? It won't publish.")) return;
      try {
        await api.unschedule(id);
        await refresh();
      } catch (err) {
        // Surface the failure rather than leaving the post silently un-cancelled
        // (the Calendar's inline handler shows its own local error; this covers
        // the banner surface, which routes here too).
        setBanner({
          kind: "err",
          text: err instanceof Error ? err.message : "Couldn't cancel that post.",
        });
      }
    },
    [refresh],
  );

  // Add or remove one platform across every scheduled post in the workspace,
  // then reload so the calendar reflects it. TikTok carries its Direct-Post
  // options (the bar picks them) since TikTok can't be added without them.
  const handleBulkPlatform = useCallback(
    async (platform: PlatformId, action: "add" | "remove", tiktok?: TikTokOptions) => {
      const result = await api.bulkSetPlatform(platform, action, tiktok);
      await refresh();
      return result;
    },
    [refresh],
  );

  // Reopen a scheduled post in the composer; saving there reschedules it.
  const handleEditScheduled = useCallback((post: ScheduledPost) => {
    setEditingPost(post);
    setNav("compose");
  }, []);

  // Re-post a failed dispatch: reopen the composer with the original draft as a
  // FRESH post, pre-selecting only the platforms that FAILED (so the ones that
  // already published aren't duplicated). Needs a persisted draft (pre-8/27 posts
  // don't have one; those records won't show the Re-post button).
  const handleRepost = useCallback((rec: DispatchRecord) => {
    if (!rec.draft) return;
    const failed = rec.results.filter((r) => !r.ok).map((r) => r.platform);
    const platforms =
      failed.length > 0 ? failed : rec.platforms ?? rec.results.map((r) => r.platform);
    setEditingPost(null);
    setRepostFrom({ draft: rec.draft, platforms, nonce: Date.now() });
    setNav("compose");
  }, []);

  // Smart Retry: re-send a failed post's transient legs immediately (no composer)
  // under a FRESH idempotency key, so it's a genuine new attempt rather than a
  // deduped no-op. Only offered when every failed leg is retryable (see
  // isRetryable) — otherwise the UI shows Re-post instead.
  const handleRetry = useCallback(
    async (rec: DispatchRecord) => {
      if (!rec.draft) return;
      const failed = rec.results.filter((r) => !r.ok).map((r) => r.platform);
      if (failed.length === 0) return;
      try {
        await api.dispatch(failed, rec.draft, crypto.randomUUID());
        pushToast({ kind: "ok", text: "Retrying — it'll appear in your calendar shortly." });
        await refresh();
      } catch (e) {
        pushToast({
          kind: "err",
          text: e instanceof Error ? e.message : "Couldn't retry that post.",
        });
      }
    },
    [refresh, pushToast],
  );

  /** Point the client at a workspace, persist it, and reload that brand's data.
   * Switching brands resets the view to the Dashboard: whatever page you were on
   * (a half-finished compose, another brand's Analytics) rarely makes sense
   * against the new workspace, so land on the one page that always does. Editing
   * state is cleared for the same reason. */
  const selectWorkspace = useCallback(
    (id: string) => {
      if (id === activeWorkspaceId) return;
      setActiveWorkspaceId(id);
      setActiveWorkspaceIdState(id);
      setEditingPost(null);
      setNav("calendar");
      void refresh();
    },
    [activeWorkspaceId, refresh],
  );

  // After auth, load the user's workspaces and settle on an active one BEFORE
  // fetching accounts/scheduled/history, so every request carries the right
  // workspace. Keep the stored id if it still exists, else fall back to the first.
  useEffect(() => {
    if (!session) {
      setData(null);
      setWorkspaces([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.workspaces();
        if (cancelled) return;
        setWorkspaces(list);
        const stored = getActiveWorkspaceId();
        const active = list.find((w) => w.id === stored) ?? list[0] ?? null;
        setActiveWorkspaceId(active?.id ?? null);
        setActiveWorkspaceIdState(active?.id ?? null);
        await refresh();
      } catch {
        /* not signed in yet */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, refresh]);

  const handleCreateWorkspace = useCallback(
    async (name: string) => {
      const created = await api.createWorkspace(name);
      setWorkspaces(await api.workspaces());
      selectWorkspace(created.id);
    },
    [selectWorkspace],
  );

  const handleRenameWorkspace = useCallback(async (id: string, name: string) => {
    await api.renameWorkspace(id, name);
    setWorkspaces(await api.workspaces());
  }, []);

  const handleDeleteWorkspace = useCallback(
    async (id: string) => {
      await api.deleteWorkspace(id);
      const list = await api.workspaces();
      setWorkspaces(list);
      // If we deleted the active workspace, switch to another one.
      if (id === activeWorkspaceId) {
        const next = list[0] ?? null;
        setActiveWorkspaceId(next?.id ?? null);
        setActiveWorkspaceIdState(next?.id ?? null);
        void refresh();
      }
    },
    [activeWorkspaceId, refresh],
  );

  // Surface the result of an OAuth round-trip (?oauth=connected|error&detail=…)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get("oauth");
    if (!oauth) return;
    const detail = params.get("detail") || "";
    // The callback tells us which workspace the connection was actually saved to.
    // Land there, not on whatever was last active — connecting from a brand-new
    // workspace used to bounce you back to the previous one (and hide the new
    // account). Persisting it here also stops the workspace-load effect from
    // reverting to the stale stored id.
    const returnedWs = params.get("ws");
    if (returnedWs && returnedWs !== activeWorkspaceId) {
      setActiveWorkspaceId(returnedWs);
      setActiveWorkspaceIdState(returnedWs);
    }
    if (oauth === "connected" && detail === "facebook") {
      // Facebook connects the user but still needs a Page chosen before it can
      // publish. Prompt for it immediately with an actionable (sticky) toast, so
      // the connection isn't silently left half-done.
      pushToast({
        kind: "warn",
        text: "Facebook connected — now pick which Page to publish to.",
        action: { label: "Choose Page", onClick: () => setNav("connections") },
      });
    } else if (oauth === "connected") {
      pushToast({ kind: "ok", text: `Connected to ${PLATFORM_LABEL[detail] ?? detail}.` });
    } else {
      pushToast({ kind: "err", text: `Connection failed: ${decodeURIComponent(detail)}` });
    }
    window.history.replaceState({}, "", window.location.pathname);
    void refresh();
  }, [refresh, pushToast, activeWorkspaceId]);

  // Surface the result of a Stripe checkout round-trip (?billing=success|topup|cancel)
  useEffect(() => {
    const billing = new URLSearchParams(window.location.search).get("billing");
    if (!billing) return;
    const text =
      billing === "success"
        ? "Subscription active."
        : billing === "topup"
          ? "Credits added."
          : "Checkout canceled.";
    setBanner({ kind: billing === "cancel" ? "err" : "ok", text });
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  if (!ready) return <div className="center muted">Loading…</div>;
  // A reset-link recovery session takes precedence over everything: the user is
  // here to choose a new password, not to use the app. Needs the session to call
  // updateUser, so it renders only once one exists.
  if (recovery && session) {
    return (
      <ResetPassword
        onDone={() => setRecovery(false)}
        onCancel={() => {
          setRecovery(false);
          void supabase.auth.signOut();
        }}
      />
    );
  }
  // `mfaPending` keeps the Login component mounted for the second-factor step;
  // Login itself renders the 6-digit challenge when the session is at aal1.
  if (!session || mfaPending) return <Login />;

  const email = session.user.email ?? undefined;
  // A name the user set in Settings (auth user_metadata) wins over the one we
  // guess from their email; the greeting uses its first word so "Curtis Holmes"
  // still reads "Good evening, Curtis".
  const savedName = ((session.user.user_metadata?.name as string | undefined) ?? "").trim();
  const firstName = savedName ? (savedName.split(/\s+/)[0] ?? savedName) : firstNameFromEmail(email);

  // A single WorkspaceSwitcher factory so the sidebar (rich chip) and the
  // dashboard title (classic trigger) share the exact same logic + handlers.
  const workspaceSwitcher = (subLabel?: string, showActions = true) =>
    workspaces.length > 0 ? (
      <WorkspaceSwitcher
        workspaces={workspaces}
        activeId={activeWorkspaceId}
        onSwitch={selectWorkspace}
        onCreate={handleCreateWorkspace}
        onRename={handleRenameWorkspace}
        onDelete={handleDeleteWorkspace}
        onError={(text) => pushToast({ kind: "err", text })}
        subLabel={subLabel}
        showActions={showActions}
      />
    ) : null;

  // Live derivations for the dashboard — same source data as the calendar.
  const stats = data ? computeStats(data.scheduled, data.history) : { scheduled: 0, publishedThisWeek: 0, failed: 0 };

  // Forward-looking summary for the Today band: how many posts go out in the
  // next 7 days, and when the very next one publishes.
  const upcomingSummary = (() => {
    const nowMs = Date.now();
    const weekAhead = nowMs + 7 * 24 * 60 * 60 * 1000;
    const future = data
      ? [...data.scheduled].filter((p) => p.at >= nowMs).sort((a, b) => a.at - b.at)
      : [];
    return {
      soon: future.filter((p) => p.at <= weekAhead).length,
      nextAt: future[0]?.at ?? null,
    };
  })();

  const calendarView = data && (
    <>
      <DashboardHeader
        greeting={greeting()}
        firstName={firstName}
        stats={stats}
        credits={billing?.configured ? billing.creditsBalance : null}
        onManageCredits={() => setNav("settings")}
        onViewFailed={() => setNav("activity")}
        workspaceSwitcher={workspaceSwitcher(undefined, false)}
        onCreate={openCompose}
      />

      {/* First-run welcome tour: a one-time intro overlay for a brand-new account
          (no accounts, no posts, not previously dismissed). It sits over the
          dashboard once, then the persistent onboarding checklist below guides
          the rest. Dismissal is remembered per-account in localStorage. */}
      {(() => {
        const tourKey = `broadside.tour.${email ?? "anon"}`;
        let seen = true;
        try {
          seen = localStorage.getItem(tourKey) === "1";
        } catch {
          seen = true;
        }
        const brandNew =
          data.accounts.length === 0 && data.history.length === 0 && data.scheduled.length === 0;
        if (tourDismissed || seen || !brandNew) return null;
        return (
          <WelcomeTour
            firstName={firstName}
            onGetStarted={() => setNav("connections")}
            onDismiss={() => {
              try {
                localStorage.setItem(tourKey, "1");
              } catch {
                /* ignore */
              }
              setTourDismissed(true);
            }}
          />
        );
      })()}

      {/* First-run onboarding: only for an account that has never posted — a
          stepped connect → create → review flow instead of an empty calendar.
          Once anything is published or scheduled it disappears for good (a later
          disconnect is surfaced by Needs-attention, not by re-onboarding). Step 1
          until a channel is connected, then step 2 to create the first post. */}
      {data.history.length === 0 && data.scheduled.length === 0 && (
        <Onboarding
          step={data.accounts.length === 0 ? 1 : 2}
          onConnect={() => setNav("connections")}
          onCreate={openCompose}
        />
      )}

      {/* Next post beside a compact analytics bubble — the two "what's happening"
          cards share the top row (they stack on narrow screens). */}
      <div className={`dash-top-row${data.scheduled.length === 0 ? " dash-top-row--solo" : ""}`}>
        <StatusBanner
          scheduled={data.scheduled}
          platforms={data.platforms}
          accounts={data.accounts}
          onCancel={handleCancelScheduled}
          onEdit={handleEditScheduled}
        />
        <AnalyticsBubble history={data.history} onViewAnalytics={() => setNav("analytics")} />
      </div>

      {/* Connections strip — compact "what's connected" row (secondary info). */}
      <div>
        <p className="cc-section-label">Connections</p>
        <ConnectionBar
          platforms={data.platforms}
          accounts={data.accounts}
          onManage={() => setNav("connections")}
        />
      </div>

      {/* Today band on top, full-width calendar below it. */}
      <div className="cc-cal-layout">
        <TodayRail
          variant="top"
          upcoming={upcomingSummary}
          today={new Date()}
          todayPosts={todaysScheduled(data.scheduled)}
          platforms={data.platforms}
          reconnects={reconnectAccounts(data.accounts)}
          failures={recentFailures(
            data.history.filter((r) => !dismissedFailures.has(r.id)),
          )}
          onSelectPost={handleEditScheduled}
          onReconnect={() => setNav("connections")}
          onViewFailure={handleViewFailure}
          onDismissFailure={handleDismissFailure}
          onViewFullDay={() => setNav("planner")}
        />

        <Calendar
          scheduled={data.scheduled}
          history={data.history.filter((r) => !clearedPosts.has(r.id))}
          platforms={data.platforms}
          accounts={data.accounts}
          onCancelScheduled={handleCancelScheduled}
          onEdit={handleEditScheduled}
          onRepost={handleRepost}
          onRetry={handleRetry}
          onComposeRequest={openCompose}
          onClearPost={handleClearPost}
          clearedCount={clearedPosts.size}
          onRestoreCleared={handleRestoreCleared}
          focusItem={calendarFocus}
          timezone={timezone}
        />
        <RecentPerformance
          records={data.history}
          platforms={data.platforms}
          onViewAnalytics={() => setNav("analytics")}
        />
      </div>
    </>
  );

  return (
    <div className={`cc-app${collapsed ? " is-collapsed" : ""}`}>
      <Sidebar
        nav={nav}
        onNav={(key) => {
          // "Create post" must always start a fresh, blank post — route it through
          // openCompose so it clears any lingering edit/re-post target instead of
          // dropping the user back into a post they were previously editing.
          if (key === "compose") openCompose();
          else setNav(key);
        }}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapse}
        workspaceSlot={workspaceSwitcher(firstName)}
        notifUnread={unread}
        isOwner={Boolean(data?.isOwner)}
        onHelp={() => setHelpOpen(true)}
      />

      <div className="cc-main">
        <TopBar
          email={email}
          name={savedName}
          unread={unread}
          onOpenSearch={() => setPaletteOpen(true)}
          onOpenNotifications={openNotifications}
          onSettings={() => setNav("settings")}
          onGuide={() => setGuideOpen(true)}
          onSignOut={() => void supabase.auth.signOut()}
        />

        <main className="cc-content">
          {/* Transient top-of-screen toasts (connect/publish/billing + actionable
              prompts like "select a Facebook Page"). */}
          <Toasts toasts={toasts} onDismiss={dismissToast} />


          {/* Data is still loading on first paint (the session resolved but the
              dashboard fetch hasn't landed). Show a loading bar + skeleton so the
              workspace never looks like a bare footer for a few seconds. */}
          {!data ? (
            <div className="cc-loading" role="status" aria-live="polite">
              <div className="cc-loading-bar" aria-hidden="true">
                <span />
              </div>
              <div className="cc-skeleton" aria-hidden="true">
                <div className="sk sk-title" />
                <div className="sk sk-chips" />
                <div className="sk sk-hero" />
              </div>
              <span className="sr-only">Loading your workspace…</span>
            </div>
          ) : (
            <>
              {nav === "calendar" && calendarView}

              {nav === "planner" && data && (
                <section aria-label="Calendar">
                  <header className="cc-page-head">
                    <h1>
                      Planner
                      {activeBrandName && <span className="cc-page-brand">{activeBrandName}</span>}
                    </h1>
                    <p className="muted">
                      Your whole month at a glance — every scheduled and published post,
                      laid out by day. Click any day to see or add posts.
                    </p>
                  </header>
                  <BulkPlatformBar
                    scheduled={data.scheduled}
                    platforms={data.platforms}
                    accounts={data.accounts}
                    onApply={handleBulkPlatform}
                  />
                  <Calendar
                    defaultView="month"
                    scheduled={data.scheduled}
                    history={data.history.filter((r) => !clearedPosts.has(r.id))}
                    platforms={data.platforms}
                    accounts={data.accounts}
                    onCancelScheduled={handleCancelScheduled}
                    onEdit={handleEditScheduled}
                    onRepost={handleRepost}
                    onRetry={handleRetry}
                    onComposeRequest={openCompose}
                    onClearPost={handleClearPost}
                    clearedCount={clearedPosts.size}
                    onRestoreCleared={handleRestoreCleared}
                    focusItem={calendarFocus}
                    timezone={timezone}
                  />
                </section>
              )}

              {/* Create post is its own full page (not a dismissable drawer), so a
                  stray click can't discard an in-progress post. Reached from the
                  sidebar, the header CTA, or the N shortcut; you leave via the
                  sidebar like any other page. */}
              {nav === "compose" && (
                <section
                  className="cc-settings-page"
                  aria-label={editingPost ? "Edit scheduled post" : "Create post"}
                >
                  <header className="cc-page-head">
                    <h1>{editingPost ? "Edit scheduled post" : "Create post"}</h1>
                    <p className="muted">
                      Write once — Broadside tailors the hook, caption, and title for each platform
                      you pick, then publishes everywhere. Or import a whole folder at once.
                    </p>
                  </header>
                  <Composer
                    platforms={data.platforms}
                    accounts={data.accounts}
                    uploadConfigured={data.uploadConfigured}
                    aiConfigured={data.aiConfigured}
                    timezone={timezone}
                    editPost={editingPost}
                    repostFrom={repostFrom}
                    onExitEdit={() => {
                      setEditingPost(null);
                      setRepostFrom(null);
                    }}
                    scheduled={data.scheduled}
                    workspaceId={activeWorkspaceId}
                    onDispatched={refresh}
                    creditsBalance={billing?.configured ? billing.creditsBalance : null}
                    onBuyCredits={() => {
                      setEditingPost(null);
                      setNav("settings");
                    }}
                    onPublishInFlight={setInFlightKey}
                    onViewActivity={() => {
                      setEditingPost(null);
                      setNav("activity");
                    }}
                    onViewCalendar={() => {
                      setEditingPost(null);
                      setNav("planner");
                    }}
                    onHide={() => {
                      // Leave the compose page to the Dashboard while the publish
                      // finishes in the background; the in-flight tracker then
                      // toasts the outcome (nav is no longer "compose").
                      setEditingPost(null);
                      setNav("calendar");
                    }}
                    onConnectAccounts={() => setNav("connections")}
                  />
                </section>
              )}

              {/* Connections is its own top-level page — the most-used surface
                  (and the first onboarding step), so it's not buried in Settings. */}
              {nav === "connections" && (
                <section className="cc-settings-page" aria-label="Connections">
                  <header className="cc-page-head">
                    <h1>Connections</h1>
                    <p className="muted">
                      Connect the accounts you publish to — YouTube, TikTok, Instagram, Facebook,
                      and X. Each workspace holds one account per platform; run more brands by
                      adding workspaces on Pro or Agency.
                    </p>
                  </header>
                  <div className="cc-settings-sections">
                    <Accounts
                      platforms={data.platforms}
                      accounts={data.accounts}
                      onChange={refresh}
                      otherWorkspaceConnections={data.otherWorkspaceConnections}
                      resolveWorkspace={resolveWorkspaceName}
                    />
                  </div>
                </section>
              )}

              {nav === "analytics" && (
                <section aria-label="Analytics">
                  <header className="cc-page-head">
                    <h1>Analytics</h1>
                    <p className="muted">
                      How your posts performed across every platform — total reach and engagement,
                      plus which platform carried each one.
                    </p>
                  </header>
                  <Analytics history={data.history} platforms={data.platforms} />
                </section>
              )}

              {nav === "activity" && (
                <section className="cc-settings-page" aria-label="Activity">
                  <header className="cc-page-head">
                    <h1>
                      Activity
                      {activeBrandName && <span className="cc-page-brand">{activeBrandName}</span>}
                    </h1>
                    <p className="muted">
                      Every post {activeBrandName ? `${activeBrandName} has` : "you've"} sent —
                      published and failed — in one place. Failures stay here with a suggested fix;
                      clearing a notification never removes anything from this log.
                    </p>
                  </header>
                  <Activity
                    history={data.history}
                    platforms={data.platforms}
                    onRepost={handleRepost}
                    onRetry={handleRetry}
                    timezone={timezone}
                  />
                </section>
              )}

              {nav === "notifications" && (
                <section className="cc-settings-page" aria-label="Notifications">
                  <header className="cc-page-head">
                    <h1>Notifications</h1>
                    <p className="muted">
                      Failed publishes, accounts that need reconnecting, and other things worth
                      knowing — with a link to fix each one.
                    </p>
                  </header>
                  <div className="cc-settings-sections">
                    <Notifications
                      notifications={notifications}
                      onDismiss={handleDismissNotification}
                      onMarkAllRead={handleMarkAllRead}
                      onNavigate={handleNotificationNavigate}
                      resolveWorkspace={resolveWorkspaceName}
                    />
                  </div>
                </section>
              )}

              {/* Command Center — owner-only god-view, a full in-frame page (the
                  endpoint enforces the real gate; the nav item is owner-gated). */}
              {nav === "ops" && data.isOwner && (
                <section className="cc-ops-page" aria-label="Command Center">
                  <header className="cc-page-head">
                    <h1>Command Center</h1>
                    <p className="muted">
                      Everything across every user — growth, system health, live activity, top users,
                      and the waitlist. Click any failure to diagnose it.
                    </p>
                  </header>
                  <OpsDashboard platforms={data.platforms} />
                </section>
              )}

              {/* Settings is a full in-frame page (not a slide-over): the Control
                  Center content rendered inline in the workspace area. */}
              {nav === "settings" && (
                <section className="cc-settings-page" aria-label="Settings">
                  <header className="cc-page-head">
                    <h1>Settings</h1>
                    <p className="muted">
                      Manage billing, notifications, appearance, and security.
                    </p>
                  </header>
                  <div className="cc-settings-sections">
                    <ControlCenter
                      email={email}
                      name={savedName}
                      onError={(text) => setBanner({ kind: "err", text })}
                      timezone={timezone}
                      onSetTimezone={handleSetTimezone}
                    />
                  </div>
                </section>
              )}
            </>
          )}

          <footer className="foot muted">
            Broadside ·{" "}
            <a href="https://broadside.social/privacy.html" target="_blank" rel="noreferrer">
              Privacy
            </a>
            {" · "}
            <a href="https://broadside.social/terms.html" target="_blank" rel="noreferrer">
              Terms
            </a>
          </footer>
        </main>
      </div>

      {/* Command palette (⌘K) */}
      {data && (
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          scheduled={data.scheduled}
          history={data.history}
          onCreate={openCompose}
          onNavigate={setNav}
          onSettings={() => setNav("settings")}
          onViewFailures={openNotifications}
          onEditPost={handleEditScheduled}
        />
      )}

      {/* Notifications inbox slide-over — a quick peek from the bell; "See all"
          opens the full Notifications page. */}
      <SlideOver open={notifOpen} title="Notifications" onClose={() => setNotifOpen(false)}>
        <Notifications
          notifications={notifications}
          onDismiss={handleDismissNotification}
          onMarkAllRead={handleMarkAllRead}
          onNavigate={handleNotificationNavigate}
          resolveWorkspace={resolveWorkspaceName}
        />
        <div className="notif-seeall">
          <button
            type="button"
            className="link-btn"
            onClick={() => {
              setNotifOpen(false);
              setNav("notifications");
            }}
          >
            See all notifications →
          </button>
        </div>
      </SlideOver>

      {/* How-to guide slide-over — idiot-proof onboarding, always reachable. */}
      <SlideOver open={guideOpen} title="How Broadside works" onClose={() => setGuideOpen(false)}>
        <Guide
          onConnect={() => {
            setGuideOpen(false);
            setNav("connections");
          }}
        />
      </SlideOver>

      {/* Help & FAQ slide-over — curated, searchable answers, reachable from the
          sidebar. An answer's action jumps to the relevant page and closes it. */}
      <SlideOver open={helpOpen} title="Help & FAQ" onClose={() => setHelpOpen(false)}>
        <HelpPanel
          onNavigate={(key) => {
            setHelpOpen(false);
            setEditingPost(null);
            setNav(key);
          }}
        />
      </SlideOver>
    </div>
  );
}
